import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { alpha, SvgIcon, useTheme } from '@mui/material';
import { getFormattedDateString } from '@zeta-core';
import { ClockCircleBold, InfoIcon, WarningNavIcon } from '@zeta-core/assets';
import { IEmptyRequestResponse, IGenericErrorResponse, INotificationAppView, IUpdateUserResetPasswordRequestRequest } from '@zeta-types';
// ----------------------------------------------------------------------
type NotificationItemProps = {
  notification: INotificationAppView;
  updateUserResetPasswordRequestAPI: UseMutationResult<AxiosResponse<IEmptyRequestResponse, any>, Error, IUpdateUserResetPasswordRequestRequest, unknown>;
  isNotDrawer?: boolean;
  markRead?: UseMutationResult<
    AxiosResponse<IEmptyRequestResponse, any>,
    IGenericErrorResponse,
    {
      notificationIDs: Array<string>;
      isReadAll: boolean;
    },
    unknown
  >;
};

export default function NotificationItem({ notification, updateUserResetPasswordRequestAPI, isNotDrawer, markRead }: NotificationItemProps) {
  const theme = useTheme();
  const isUnread = notification.notificationStatus === 'Unread';

  const getBGColor = (type, section) => {
    switch (type) {
      case 'Info':
        if (section === 'icon') return alpha(theme.palette.info.main, 0.16);
        else if (section === 'badge') return theme.palette.info.main;
        return alpha(theme.palette.info.main, 0.08);
      case 'Reminder':
        if (section === 'icon') return alpha(theme.palette.warning.main, 0.16);
        else if (section === 'badge') return theme.palette.warning.main;
        return alpha(theme.palette.warning.main, 0.08);
      case 'Warning':
        if (section === 'icon') return alpha(theme.palette.error.main, 0.16);
        else if (section === 'badge') return theme.palette.error.main;
        return alpha(theme.palette.error.main, 0.08);
      default:
        if (section === 'icon') return alpha(theme.palette.info.main, 0.16);
        else if (section === 'badge') return theme.palette.info.main;
        return alpha(theme.palette.info.main, 0.08);
    }
  };

  const getNotiIcon = (type) => {
    switch (type) {
      case 'Info':
        return <InfoIcon color={`${theme.palette.info.dark}`} />;
      case 'Reminder':
        return (
          <SvgIcon height="8">
            <ClockCircleBold />
          </SvgIcon>
        );
      case 'Warning':
        return <WarningNavIcon />;
      default:
        return <InfoIcon color={`${theme.palette.info.dark}`} />;
    }
  };

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: getBGColor(notification.appNotificationType, 'icon'),
        }}
      >
        {getNotiIcon(notification.appNotificationType)}
      </Stack>
    </ListItemAvatar>
  );
  const renderText = (
    <ListItemText
      disableTypography
      sx={{
        color: 'text.primary',
        m: 0,
      }}
      primary={reader(notification.message)}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.secondary', mt: 0.5 }}
          justifyContent="space-between"
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          {getFormattedDateString(notification.date, 'DD MMM YYYY')}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = (
    <Box
      sx={{
        top: '50%',
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: getBGColor(notification.appNotificationType, 'badge'),
        position: 'absolute',
      }}
    />
  );

  const advisorResetPasswordAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      {notification?.resetPasswordAuthStatus !== 'Pending' && (
        <Button size="small" variant="contained" disabled>
          {notification?.resetPasswordAuthStatus}
        </Button>
      )}

      {notification?.resetPasswordAuthStatus === 'Pending' && (
        <>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => {
              const payload: IUpdateUserResetPasswordRequestRequest = {
                comment: 'Authorised',
                sessionToken: notification.sessionToken,
                authorisationStatus: 'Authorised',
              };
              updateUserResetPasswordRequestAPI.mutateAsync(payload);
            }}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              const payload: IUpdateUserResetPasswordRequestRequest = {
                comment: 'Rejected',
                sessionToken: notification.sessionToken,
                authorisationStatus: 'Rejected',
              };
              updateUserResetPasswordRequestAPI.mutateAsync(payload);
            }}
          >
            Reject
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2,
        mb: isNotDrawer ? 0 : 0.75,
        alignItems: 'flex-start',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, isUnread ? 0.2 : 0.12)}`,
        borderBottom: isNotDrawer ? `1px solid ${alpha(theme.palette.divider, 0.16)}` : undefined,
        background: isUnread
          ? `linear-gradient(135deg, ${getBGColor(notification.appNotificationType, 'background')} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
          : theme.palette.background.paper,
        boxShadow: isUnread ? `0 10px 24px ${alpha(theme.palette.common.black, 0.05)}` : 'none',
        '&:hover': {
          background: isUnread
            ? `linear-gradient(135deg, ${alpha(getBGColor(notification.appNotificationType, 'badge'), 0.12)} 0%, ${theme.palette.background.paper} 100%)`
            : alpha(theme.palette.action.hover, 0.72),
        },
      }}
      onClick={() => {
        if (isUnread) markRead?.mutateAsync({ notificationIDs: [notification.id], isReadAll: false });
      }}
    >
      {isUnread && renderUnReadBadge}
      {renderAvatar}
      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {notification.notificationType === 'AdvisorResetPassword' && advisorResetPasswordAction}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------
function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        color: 'text.primary',
        '& p': { typography: 'body2', m: 0, color: 'inherit' },
        '& a': { color: 'primary.main', textDecoration: 'none', fontWeight: 700 },
        '& strong': { typography: 'subtitle2', color: 'inherit' },
      }}
    />
  );
}
